import { useState, useEffect, useRef } from 'react';
import { Application, ApplicationDto, ApplicationListResponse, RoleDto } from '../types/auth';
import { getIdMeBaseApiUrl } from '../utils/UrlConstants';
import { useMsal } from '@azure/msal-react';
import { fetchUserGroups, fetchMicrosoftGraphGroups } from '../utils/groupUtils';
import { useIdentityAppService } from '../services/IdentityServices';

export const useApplications = () => {
  const { instance, accounts } = useMsal();
  const [applications, setApplications] = useState<Application[]>([]);
  const [groupNames, setGroupNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [dynamicApplications, setDynamicApplications] = useState<ApplicationDto[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);
  const loadInProgressRef = useRef(false);
  const retryCountRef = useRef(0);

  // Determine if user is internal (MSAL authenticated) or external (B2C authenticated)
  const isInternalUser = () => {
    return accounts && accounts.length > 0;
  };

  // Fetch groups based on user type (internal vs external)
  const fetchGroups = async () => {
    try {
      if (isInternalUser()) {
        // Internal user - use Microsoft Graph
        console.log('Fetching groups for internal user via Microsoft Graph');
        const account = accounts[0];
        const tokenRequest = {
          scopes: ['User.Read', 'Group.Read.All'],
          account: account,
        };

        // Check if the token is already in sessionStorage
        let accessToken = sessionStorage.getItem('msalToken');
        if (!accessToken) {
          const response = await instance.acquireTokenSilent(tokenRequest);
          accessToken = response.accessToken;
          sessionStorage.setItem('msalToken', accessToken);
        }

        const groups = await fetchMicrosoftGraphGroups(accessToken);
        setUserGroups(groups);
        setGroupNames(groups);
        return groups;
      } else {
        // External user - use fetchUserGroups from session or API
        console.log('Fetching groups for external user');

        // First try to get from session storage
        const externalGroups = sessionStorage.getItem('external_groups');
        if (externalGroups) {
          const sessionGroups = externalGroups.split(",").filter(group => group.trim() !== '');
          setUserGroups(sessionGroups);
          setGroupNames(sessionGroups);
          console.log('Using external groups from session:', sessionGroups);
          return sessionGroups;
        }

        // If not in session, try to get from external token (non-blocking approach)
        const externalToken = sessionStorage.getItem('external_token');
        if (externalToken) {
          try {
            const parsed = JSON.parse(externalToken);

            // Use token groups immediately if available
            // if (parsed.groupNames && parsed.groupNames.length > 0) {
            //   console.log('Using groups from external token immediately:', parsed.groupNames);
            //   setUserGroups(parsed.groupNames);
            //   setGroupNames(parsed.groupNames);
            //   sessionStorage.setItem('external_groups', parsed.groupNames.join(','));

            //   // Fetch API groups in background (don't await)
            //   if (parsed.sub) {
            //     fetchUserGroups(parsed.sub).then(apiGroups => {
            //       if (apiGroups.length > 0) {
            //         console.log('Updated groups from API:', apiGroups);
            //         setUserGroups(apiGroups);
            //         setGroupNames(apiGroups);
            //         sessionStorage.setItem('external_groups', apiGroups.join(','));
            //       }
            //     }).catch(error => {
            //       console.error('Background group fetch failed:', error);
            //     });
            //   }

            //   return parsed.groupNames;
            // }

            if (parsed.groupNames && parsed.groupNames.length > 0) {
              const tokenGroups = parsed.groupNames;
              console.log('Using groups from external token immediately:', tokenGroups);
              setUserGroups(tokenGroups);
              setGroupNames(tokenGroups);
              sessionStorage.setItem('external_groups', tokenGroups.join(','));

              if (parsed.sub) {
                try {
                  const apiGroups = await Promise.race([
                    fetchUserGroups(parsed.sub),
                    new Promise<string[]>((_, reject) =>
                      setTimeout(() => reject(new Error('Timeout')), 5000)
                    )
                  ]) as string[];

                  if (apiGroups && apiGroups.length > 0) {
                    console.log('Updated groups from API:', apiGroups);
                    setUserGroups(apiGroups);
                    setGroupNames(apiGroups);
                    sessionStorage.setItem('external_groups', apiGroups.join(','));
                    return apiGroups;
                  }
                } catch (error) {
                  console.warn('API group fetch failed, using token groups:', error);
                }
              }

              return tokenGroups;
            }

            // If no token groups, try API call with timeout
            if (parsed.sub) {
              console.log('No token groups, trying API call with timeout...');
              try {
                const apiGroups = await Promise.race([
                  fetchUserGroups(parsed.sub),
                  new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Timeout')), 3000)
                  )
                ]) as string[];

                if (apiGroups.length > 0) {
                  setUserGroups(apiGroups);
                  setGroupNames(apiGroups);
                  sessionStorage.setItem('external_groups', apiGroups.join(','));
                  return apiGroups;
                }
              } catch (error) {
                console.warn('API group fetch timed out or failed, proceeding without groups:', error);
              }
            }
          } catch (error) {
            console.error('Error parsing external token:', error);
          }
        }

        console.log('No external groups found, proceeding with empty groups');
        return [];
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      return [];
    }
  };

  // Fetch applications with roles from backend
  const fetchApplicationsWithRoles = async (attempt = 0): Promise<ApplicationDto[]> => {
    try {
      const response = await useIdentityAppService().getExternalAccessApplications();

      if (response.ok) {
        const data: ApplicationListResponse = await response.json();
        if (data.success && data.applications) {
          return data.applications;
        } else {
          console.error('Failed to fetch applications:', data.message);
          setError('Failed to fetch applications: ' + data.message);
          return [];
        }
      } else {
        console.error('Failed to fetch applications:', response.status);
        if (attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          return fetchApplicationsWithRoles(attempt + 1);
        }
        setError('Failed to fetch applications: ' + response.status);
        return [];
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      if (attempt < 2) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchApplicationsWithRoles(attempt + 1);
      }
      setError('Error fetching applications: ' + (error as Error).message);
      return [];
    }
  };


  // Filter applications based on user groups
  const filterApplicationsByGroups = (apps: ApplicationDto[], userGroupNames: string[]): ApplicationDto[] => {
    // If no groups available yet, return all external access apps (better UX)
    if (!userGroupNames || userGroupNames.length === 0) {
      console.log('No groups available, showing all external access applications');
      console.log('Available apps:', apps.map(app => ({ name: app.applicationName, allowExternal: app.allowExternalAccess })));
      const externalApps = apps.filter(app => {
        const allowed = app.allowExternalAccess;
        return allowed === 'Y' || allowed === 'yes' || allowed === 'true' || allowed === '1' || allowed === 'YES';
      });
      console.log('Filtered external apps:', externalApps.length);
      return externalApps;
    }

    return apps.filter(app => {
      if (!app.roles || app.roles.length === 0) {
        return false;
      }

      // Check if any of the app's roles match user's groups
      const hasMatchingRole = app.roles.some(role => {
        if (role.roleName) {
          const roleNameLower = role.roleName.toLowerCase();
          return userGroupNames.some(groupName => groupName.toLowerCase() === roleNameLower);
        }
        return false;
      });

      return hasMatchingRole;
    });
  };

  // Convert ApplicationDto with environments to Application format, filtering by user access
  const convertToApplicationFormat = (apps: ApplicationDto[], userGroupNames: string[]): Application[] => {
    const applications: Application[] = [];

    apps.forEach(app => {
      if (app.environments && app.environments.length > 0) {
        // Filter environments based on user's role access
        const accessibleEnvironments = app.environments.filter(env => {
          if (!env.roles || env.roles.length === 0) {
            return false; // No roles defined for this environment
          }

          // Check if user has any role for this specific environment
          return env.roles.some(role => {
            if (role.roleName) {
              const roleNameLower = role.roleName.toLowerCase();
              return userGroupNames.some(groupName => groupName.toLowerCase() === roleNameLower);
            }
            return false;
          });
        });

        // Create separate tiles only for accessible environments
        accessibleEnvironments.forEach(env => {
          applications.push({
            applicationName: `${app.applicationName} (${env.envName})`,
            applicationDescription: env.envDesc || app.applicationDesc || 'Click to access this application',
            applicationUrl: env.uri || null
          });
        });
      } else {
        // Fallback for applications without environments - check app-level roles
        if (app.roles && app.roles.length > 0) {
          const hasAccess = app.roles.some(role => {
            if (role.roleName) {
              const roleNameLower = role.roleName.toLowerCase();
              return userGroupNames.some(groupName => groupName.toLowerCase() === roleNameLower);
            }
            return false;
          });

          if (hasAccess) {
            applications.push({
              applicationName: app.applicationName,
              applicationDescription: app.applicationDesc || 'Click to access this application',
              applicationUrl: null
            });
          }
        }
      }
    });

    return applications;
  };

  // Load applications and user groups for both internal and external users
  const loadDynamicApplications = async () => {
    if (loadInProgressRef.current) return;

    loadInProgressRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('Loading dynamic applications...');
      console.log('Is internal user:', isInternalUser());

      const appsPromise = fetchApplicationsWithRoles();
      const userGroupList = await fetchGroups();
      const apps = await appsPromise;

      const filteredApps = filterApplicationsByGroups(apps, userGroupList);
      const convertedApps = convertToApplicationFormat(filteredApps, userGroupList);

      setDynamicApplications(filteredApps);
      setApplications(convertedApps);

      // Important part:
      // For external users, if the first attempt returns no apps, allow one more retry
      if (!isInternalUser() && convertedApps.length === 0 && retryCountRef.current < 2) {
        retryCountRef.current += 1;
        setHasLoaded(false);
        return;
      }

      retryCountRef.current = 0;
      setHasLoaded(true);
    } catch (error) {
      console.error('Error loading dynamic applications:', error);
      setError('Error loading applications: ' + (error as Error).message);
      setApplications([]);
      setDynamicApplications([]);
    } finally {
      loadInProgressRef.current = false;
      setLoading(false);
    }
  };

  // Load applications on mount - different logic for internal vs external users
  useEffect(() => {
    const initializeApplications = async () => {
      if (hasLoaded) return; // Prevent multiple loads

      if (isInternalUser()) {
        // Internal user - load immediately
        console.log('Internal user detected, loading applications...');
        await loadDynamicApplications();
        // setHasLoaded(true);
      } else {
        // External user - check if token is available (don't wait for groups)
        const externalToken = sessionStorage.getItem('external_token');

        if (externalToken) {
          console.log('External user token found, loading applications immediately...');
          // Load applications immediately, groups will be fetched in parallel
          await loadDynamicApplications();
          // setHasLoaded(true);
        } else {
          console.log('External user without token, waiting...');
        }
      }
    };

    initializeApplications();
  }, [accounts, hasLoaded, forceRefresh]); // Added hasLoaded and forceRefresh to dependencies

  // For external users, watch for session storage changes (optimized)
  useEffect(() => {
    if (isInternalUser() || hasLoaded) return; // Skip if internal user or already loaded

    const handleStorageChange = () => {
      const externalToken = sessionStorage.getItem('external_token');

      if (externalToken && !hasLoaded && !loading) {
        console.log('External token became available, loading applications...');
        setForceRefresh(prev => prev + 1); // Trigger re-initialization
      }
    };

    // Check immediately
    handleStorageChange();

    // Listen for our custom storage update event (more efficient)
    const handleCustomStorageChange = () => {
      handleStorageChange();
    };

    window.addEventListener('sessionStorageUpdate', handleCustomStorageChange);

    // Also check a few times with shorter intervals for immediate responsiveness
    let checkCount = 0;
    const interval = setInterval(() => {
      handleStorageChange();
      checkCount++;
      if (checkCount >= 5 || hasLoaded) { // Stop after 5 checks or when loaded
        clearInterval(interval);
      }
    }, 500); // Check every 500ms for first 2.5 seconds

    return () => {
      window.removeEventListener('sessionStorageUpdate', handleCustomStorageChange);
      clearInterval(interval);
    };
  }, [hasLoaded, loading]); // Only re-run when these specific values change

  // Additional effect to ensure external users load apps when token is available
  useEffect(() => {
    if (isInternalUser() || hasLoaded) return;

    const checkToken = () => {
      const externalToken = sessionStorage.getItem('external_token');
      if (externalToken) {
        console.log('Direct token check: External token found, triggering load...');
        setForceRefresh(prev => prev + 1);
      }
    };

    // Check immediately
    checkToken();

    // Check periodically for a short time with increasing intervals
    let attempts = 0;
    const maxAttempts = 10;

    const checkWithBackoff = () => {
      if (attempts >= maxAttempts || hasLoaded) return;

      checkToken();
      attempts++;

      // Exponential backoff: 200ms, 400ms, 800ms, etc.
      const delay = Math.min(200 * Math.pow(2, attempts), 2000);
      setTimeout(checkWithBackoff, delay);
    };

    const timeoutId = setTimeout(checkWithBackoff, 200);

    return () => clearTimeout(timeoutId);
  }, []); // Run once on mount

  useEffect(() => {
    if (isInternalUser()) return;

    const handleStorageChange = () => {
      const externalToken = sessionStorage.getItem('external_token');
      if (externalToken && !loading) {
        console.log('External token became available, loading applications...');
        setForceRefresh(prev => prev + 1);
      }
    };

    handleStorageChange();

    const handleCustomStorageChange = () => {
      handleStorageChange();
    };

    window.addEventListener('sessionStorageUpdate', handleCustomStorageChange);

    const interval = setInterval(() => {
      handleStorageChange();
    }, 500);

    return () => {
      window.removeEventListener('sessionStorageUpdate', handleCustomStorageChange);
      clearInterval(interval);
    };
  }, [loading]);

  return {
    applications,
    loading,
    error,
    groupNames,
    userGroups,
    dynamicApplications,
    refreshApplications: () => {
      setHasLoaded(false); // Reset to allow re-loading
      setForceRefresh(prev => prev + 1); // Trigger re-initialization
    }
  };
};